import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { caseStudies } from '../../data/caseStudies';
import '../../styles/glass.css';

const CaseStudiesGrid = () => {
  // Transform case studies data to match the grid format
  function getIconForMetric(metric) {
    const iconMap = {
      'Sales': '💰',
      'Sales Growth': '💰',
      'Acos': '🎯',
      'ROI': '💰',
      'Rating': '⭐',
      'Growth': '📈',
      'Conversion': '🎯',
      'Visibility': '📊',
      'Listing Created': '📈',
      'Monthly Revenue': '💰',
      'Product Portfolio': '📊',
      'ROAS': '🎯',
      'ACOS': '🎯',
      'Category Rank': '📊',
    };
    return iconMap[metric] || iconMap.default;
  }
  const cases = Object.entries(caseStudies).map(([key, study]) => ({
    id: key,
    title: study.title,
    category: study.category,
    challenge: `${study.challenges[0].description.slice(0, 100)} ...`,
    solution: `${study.workDone[0].description.slice(0, 100)}...`,
    stats: [
      { metric: study.stats[0].label, value: study.stats[0].value, icon: getIconForMetric(study.stats[0].label) },
      { metric: study.stats[1].label, value: study.stats[1].value, icon: getIconForMetric(study.stats[1].label) },
      { metric: study.stats[2].label, value: study.stats[2].value, icon: getIconForMetric(study.stats[2].label) },
      { metric: study.stats[3].label, value: study.stats[3].value, icon: getIconForMetric(study.stats[3].label) }
    ],
    testimonial: {
      text: `${study.testimonial.quote.slice(0, 100)}...`,
      author: study.testimonial.author,
      role: study.testimonial.position
    },
    color: 'blue-600/20 purple-600/20 pink-600/20'
  }));

  return (
    <section className="relative pb-20 pt-10 bg-black overflow-hidden">
      <div className="container mx-auto px-4 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-2 rounded-full mb-6"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2.5 2.5 0 00-2.5-2.5H15" />
            </svg>
            <span className="text-sm font-medium">CASE STUDIES</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Client Success Stories
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-400 max-w-3xl mx-auto"
          >
            Discover how we've helped businesses transform their Amazon presence and achieve remarkable growth
          </motion.p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {cases.map((caseStudy, index) => (
            <Link 
              to={`/case-studies/${caseStudy.id}`}
              key={index}
              className="block"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative group"
              >
                <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-20 blur-2xl group-hover:opacity-30 transition-opacity duration-500"
                  style={{ background: `linear-gradient(to bottom right, ${caseStudy.color.split(' ').join(', ')})` }}
                />
                <div className="relative glass-effect rounded-3xl p-8 h-full hover:border-primary border-2 transition-colors duration-300">
                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {caseStudy.title}
                  </h3>

                  {/* Challenge & Solution */}
                  <div className="space-y-4 mb-8">
                    <div>
                      <h4 className="text-primary font-medium mb-2">Challenge</h4>
                      <p className="text-gray-400">{caseStudy.challenge}</p>
                    </div>
                    <div>
                      <h4 className="text-primary font-medium mb-2">Solution</h4>
                      <p className="text-gray-400">{caseStudy.solution}</p>
                    </div>
                  </div>

                  {/* Results Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {caseStudy.stats.map((stat, idx) => (
                      <div key={idx} className="bg-white/5 rounded-xl p-4 backdrop-blur-sm">
                        <div className="flex justify-between mb-2">
                          <span className="text-2xl font-bold text-primary">{stat.value}</span>
                          <span className="text-2xl">{stat.icon}</span>
                        </div>
                        <div className="text-sm text-gray-400">
                          {stat.metric}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Testimonial */}
                  <div className="border-t border-white/10 pt-6">
                    <blockquote className="text-gray-400 italic mb-4">
                      "{caseStudy.testimonial.text}"
                    </blockquote>
                    <div>
                      <div className="font-medium text-white">
                        {caseStudy.testimonial.author}
                      </div>
                      <div className="text-sm text-gray-500">
                        {caseStudy.testimonial.role}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesGrid;
